"use client";

import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { api } from "@/convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import {
  buildContactEnrichmentPreviewExample,
  ContactEnrichmentPreview,
} from "@/components/contacts/contact-enrichment-preview";
import { Row } from "../../_components/row/Row";
import { useQuery, useAction } from "convex/react";
import { Id } from "@/convex/_generated/dataModel";
import { Text } from "../../_components/text/Text";
import { Column } from "../../_components/column/Column";
import { Button } from "../../_components/button/button";
import { BucketerApp } from "@/app/marketing/_components/bucketer/BucketerApp";
import { ContactSyncStatus } from "./contact-sync-status";
import { NativeFubPreparing } from "./native-fub-preparing";
import { Home, Users, Phone, MapPin, Loader2, Check, ArrowRight } from "lucide-react";
import { withUser } from "@/hooks/with-user";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EnrichmentStatusProps {
  onComplete?: () => void;
  hideActions?: boolean;
  hideSample?: boolean;
  hideStats?: boolean;
  autoStart?: boolean;
  hideProgress?: boolean;
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

export function StatCard({
  icon,
  label,
  value,
  subtext,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode | string | number;
  subtext?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border p-4 flex flex-col gap-1 items-start justify-start",
        highlight ? "border-teal-200 bg-teal-50/40" : "border-border bg-white",
      )}
    >
      <Row align tightGap className="flex-none">
        {icon}
        <Text sm>{label}</Text>
      </Row>
      <Text xl fat>
        {typeof value === "number" ? value.toLocaleString() : value}
      </Text>
      {subtext && (
        <Text sm subdued>
          {subtext}
        </Text>
      )}
    </div>
  );
}

function EnrichedContactsSample({ runId, totalEnriched }: { runId: Id<"bucketerRuns">; totalEnriched: number }) {
  const contacts = useQuery(api.bucketer.queries.getRichestContacts, { runId, limit: 4 });

  if (!contacts || contacts.length === 0) return null;

  const examples = contacts.map((contact) =>
    buildContactEnrichmentPreviewExample(contact as unknown as Record<string, unknown>),
  );
  const remaining = Math.max(0, totalEnriched - contacts.length);

  return (
    <ContactEnrichmentPreview
      title="Here's what we found in your database"
      description="Real contacts from your CRM, enriched with property, equity, and demographic data."
      examples={examples}
      moreExamplesCount={remaining}
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EnrichmentStatus({
  onComplete,
  hideActions = false,
  hideSample = false,
  hideStats = false,
  autoStart = true,
  hideProgress = false,
}: EnrichmentStatusProps) {
  const { team } = withUser();

  const latestRun = useQuery(api.bucketer.queries.getLatestRun, team?._id ? { teamId: team._id } : "skip");
  const startEnrichment = useAction(api.bucketer.actions.startBucketerRunFromActivation);
  const [enrichmentTriggered, setEnrichmentTriggered] = useState(false);

  // Stable across renders so ContactSyncStatus's internal useEffect doesn't
  // tear down and restart on every parent re-render. Calling startEnrichment
  // BEFORE flipping `enrichmentTriggered` means a thrown action leaves the
  // component in a retryable state (otherwise we'd fall into BucketerApp
  // with no run, which is an unrecoverable dead end).
  const handleReady = useCallback(async () => {
    if (!autoStart) return;
    try {
      await startEnrichment({});
      setEnrichmentTriggered(true);
    } catch (err) {
      console.error("[EnrichmentStatus] Failed to start enrichment:", err);
    }
  }, [startEnrichment, autoStart]);

  if (!team?._id) return null;

  // `useQuery` returns `undefined` while loading and `null` when the query
  // resolves to no document. We must distinguish these: firing
  // ContactSyncStatus while the query is still loading can cause a duplicate
  // enrichment run on a team that already has a completed one (because the
  // first poll detects contacts, calls `onReady`, and
  // `startBucketerRunFromActivation` creates a fresh run and wipes the
  // databank) every time the activate page is visited.
  const latestRunLoading = latestRun === undefined;
  const noLatestRun = latestRun === null;

  // Only render ContactSyncStatus for Rollout-connected teams. Direct FUB
  // OAuth (provider: "fub") doesn't populate `rollout_people`, so polling
  // that table would loop forever and the user would see "taking longer
  // than expected" indefinitely. Native FUB teams take their own
  // NativeFubPreparing branch below — it waits for the FUB list-sync to
  // complete and then triggers the bucketer run via the same handleReady.
  const isRolloutConnected = team.crm?.status === "connected" && team.crm?.provider === "rollout";
  const isNativeFub = team.crm?.status === "connected" && team.crm?.provider === "fub";

  // While `latestRun` is still loading we must render nothing dynamic —
  // rendering BucketerApp here would auto-start a new enrichment run (and
  // wipe the databank) on every activate-page revisit, burning REAPI credits.
  if (latestRunLoading) {
    return (
      <Row align className="justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Row>
    );
  }

  if (team.crm?.status !== "connected") {
    return (
      <Column className="gap-6">
        <Text muted>Connect your CRM and we'll enrich your database.</Text>
      </Column>
    );
  }

  // CRM connected via Rollout but no bucketer run yet — poll for contacts first
  if (noLatestRun && !enrichmentTriggered && isRolloutConnected) {
    const crmName = team.crm?.appName || team.crm?.appKey || "CRM";
    return <ContactSyncStatus crmName={crmName} onReady={handleReady} />;
  }

  if (noLatestRun && !enrichmentTriggered && isNativeFub) {
    return <NativeFubPreparing onReady={handleReady} />;
  }

  if (!latestRun || latestRun.status !== "complete") {
    return (
      <BucketerApp
        hideHeader={true}
        showCrmConnect={false}
        teamId={team._id}
        latestRunId={latestRun?._id}
        credentialId={team.crm?.credentials?.credentialId}
      />
    );
  }

  // Guards above have already returned for every other status — only
  // `"complete"` runs reach here. Bulk-enrichment is the only dynamic state.
  const prog = latestRun.progress;
  const isBulkRunning = latestRun.bulkEnrichmentStatus === "running";

  const enrichedPct = prog.totalContacts > 0 ? Math.round((prog.contactsEnriched / prog.totalContacts) * 100) : 0;
  const totalCrm = prog.totalCrmContacts ?? prog.totalContacts;

  const withName = prog.contactsFetched;
  const withAddress = prog.addressesResolved;
  const withProperty = prog.propertiesLookedUp;
  const skipTracesFound = (latestRun as { progress: { skipTracesFound?: number } }).progress.skipTracesFound ?? 0;

  return (
    <Column className="gap-6">
      {/* Overall status banner */}
      <div className={cn("rounded-lg border px-5 py-4 flex items-center gap-3 bg-white")}>
        <Column tighterGap>
          <Row align tightGap>
            <Check className="h-5 w-5 text-teal-500 flex-none" />
            <Text sm fat>
              {isBulkRunning ? "Enriching your full database..." : "Initial enrichment complete"}
            </Text>
          </Row>
          <Text sm subdued className="pl-7">
            {isBulkRunning
              ? "We're enriching your entire database in the background. You can safely continue."
              : `We've enriched ${prog.contactsEnriched.toLocaleString()} contacts from a sample of your database's ${totalCrm.toLocaleString()} contacts. Additional enrichment is available later through admin tools.`}
          </Text>

          {/* Progress bar */}
          <div className="mt-4 px-7 pr-0">
            <Row align between className="mb-1">
              <Text xs className="text-muted-foreground">
                Enrichment progress
              </Text>
              <Text xs fat>
                {enrichedPct}%
              </Text>
            </Row>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-teal-400"
                initial={{ width: 0 }}
                animate={{ width: `${enrichedPct}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </div>
        </Column>
      </div>

      {(!hideStats || !hideSample) && (
        <>
          <Column tightGap>
            {!hideStats && (
              <>
                <Text sm className="text-muted-foreground">
                  Results
                </Text>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard
                    icon={<Users className="h-3.5 w-3.5 text-primary" />}
                    label="Contacts loaded"
                    value={prog.contactsFetched.toLocaleString()}
                    subtext={prog.contactsFetched > 0 ? `of ${totalCrm.toLocaleString()}` : undefined}
                  />
                  <StatCard
                    icon={<MapPin className="h-3.5 w-3.5 text-rose-500" />}
                    label="Addresses found"
                    value={withAddress}
                    subtext={
                      prog.contactsFetched > 0
                        ? `${Math.round((withAddress / prog.contactsFetched) * 100)}% match rate`
                        : undefined
                    }
                  />
                  <StatCard
                    icon={<Home className="h-3.5 w-3.5 text-amber-500" />}
                    label="Properties enriched"
                    value={withProperty}
                  />
                  <StatCard
                    icon={<Phone className="h-3.5 w-3.5 text-teal-500" />}
                    label="Skip traces found"
                    value={skipTracesFound}
                    subtext={skipTracesFound > 0 ? "New contact data discovered" : undefined}
                  />
                </div>
              </>
            )}

            {/* Enriched contacts sample */}
            {!hideSample && (
              <Column className="mt-4">
                <EnrichedContactsSample runId={latestRun._id} totalEnriched={prog.contactsEnriched} />
              </Column>
            )}
          </Column>
        </>
      )}
      {!hideActions && (
        <>
          {/* Actions */}
          <Row justifyEnd>
            {/* {!isBulkRunning && !isBulkComplete && totalCrm > prog.totalContacts && (
          <Button
            variant="bold"
            onClick={async () => {
              setIsEnriching(true);
              try {
                await enrichAll({ teamId });
              } finally {
                setIsEnriching(false);
              }
            }}
            disabled={isEnriching}
          >
            {isEnriching ? (
              <Row align tightGap>
                <Loader2 className="h-4 w-4 animate-spin" />
                <Text sm>Starting...</Text>
              </Row>
            ) : (
              <Row align tightGap>
                <Database className="h-4 w-4" />
                <Text sm>Enrich full database ({totalCrm.toLocaleString()} contacts)</Text>
              </Row>
            )}
          </Button>
        )} */}
            <Button variant="link" size="sm" className="w-fit mt-2" onClick={onComplete}>
              <Row align tightGap>
                <Text sm>Continue to next step</Text>
                <ArrowRight className="h-4 w-4" />
              </Row>
            </Button>
          </Row>
        </>
      )}
    </Column>
  );
}
